// src/views/folders/hooks/useBulkSelection.ts
/* eslint-disable max-len */
import { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { bulkDeleteItems } from '../../folders/slice/actions';

const MAX_BULK_OPERATIONS = 50;

export const useBulkSelection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [isBulkMode, setIsBulkMode] = useState(false);

  const toggleBulkSelection = useCallback((nodeId: string) => {
    setSelectedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }

      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedNodes.size === 0) {
      message.warning(t('folders:messages.nothingSelected'));

      return;
    }

    if (selectedNodes.size > MAX_BULK_OPERATIONS) {
      Modal.warning({
        title: t('folders:delete.tooManyTitle'),
        content: t('folders:delete.tooManyContent', { max: MAX_BULK_OPERATIONS }),
      });

      return;
    }

    Modal.confirm({
      title: t('folders:delete.bulkTitle', { n: selectedNodes.size }),
      content: t('folders:delete.bulkConfirm'),
      okText: t('common:buttons.delete'),
      cancelText: t('common:buttons.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
      // Zachowaj string dla id - tak jak oczekuje bulkDeleteItems
        const items = Array.from(selectedNodes).map((nodeId) => {
          const parts = nodeId.split('-');
          const type = parts[0] as 'folder' | 'list';

          return {
            id: nodeId, // Zostaw jako string!
            type,
          };
        });

        try {
          await dispatch(bulkDeleteItems(items) as any);
          setSelectedNodes(new Set());
          setIsBulkMode(false);
          message.success(t('folders:messages.itemsDeleted'));
        } catch (error) {
          message.error(t('folders:messages.bulkDeleteError'));
        }
      },
    });
  }, [dispatch, selectedNodes, t]);

  const exitBulkMode = useCallback(() => {
    setIsBulkMode(false);
    setSelectedNodes(new Set());
  }, []);

  const selectAll = useCallback((nodeIds: string[]) => {
    setSelectedNodes(new Set(nodeIds));
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedNodes(new Set());
  }, []);

  return {
    selectedNodes,
    isBulkMode,
    setIsBulkMode,
    toggleBulkSelection,
    handleBulkDelete,
    exitBulkMode,
    selectAll,
    deselectAll,
  };
};
